import { useEffect, useState } from "react";
import { useBeforeUnload } from "react-router-dom";
import templateService from "../../services/templateService";
import { useNotification } from "../../context/NotificationContext";

import TextEditorPro from "components/TextEditorPro";

// Material Dashboard 2 React example components
import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import ListVariable from "./ListVariable";
import ListTemplates from "./ListTemplates";
import ActionTemplate from "./ActionTemplate"
import { CircularProgress, Grid } from "@mui/material";

const Template = () => {
  const { showNotification } = useNotification();
  const [templateID, setTemplateID] = useState();
  const [templates, setTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState(false);
  const [updateTemplate, setUpdateTemplate] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(false);

  const selectedTemplate = templates.find((t) => t.id === templateID);
  const leaveMessage = "Hay cambios sin guardar. ¿Está seguro de salir sin guardar?";

  useBeforeUnload(
    (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = leaveMessage;
      }
    },
    { capture: true }
  );

  useEffect(() => {
    const handleNavigationClick = (event) => {
      if (!isDirty) return;
      const anchor = event.target && event.target.closest ? event.target.closest("a[href]") : null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
      const nextUrl = new URL(anchor.href, window.location.href);
      const sameLocation =
        nextUrl.pathname === window.location.pathname &&
        nextUrl.search === window.location.search &&
        nextUrl.hash === window.location.hash;
      if (sameLocation) return;
      const confirmLeave = window.confirm(leaveMessage);
      if (!confirmLeave) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("click", handleNavigationClick, true);
    return () => document.removeEventListener("click", handleNavigationClick, true);
  }, [isDirty, leaveMessage]);

  // Handler para cambios de contenido (solo actualiza estado local)
  const handleContentChange = (newContent) => {
    if (selectedTemplate) {
      const updatedTemplates = templates.map(template => 
        template.id === selectedTemplate.id 
          ? { ...template, content: newContent }
          : template
      );
      setTemplates(updatedTemplates);
    }
  };

  const handleSave = async (content) => {
    if (selectedTemplate) {
      try {
        await templateService.updateTemplateById({
          ...selectedTemplate,
          content: content,
        });
        setIsDirty(false);
        showNotification("Plantilla guardada correctamente", "success");
      } catch {
        showNotification("Error al guardar la plantilla", "error");
      }
    }
  };

  const handleNameUpdate = (id, newName) =>
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, name: newName } : t)));

  useEffect(() => {
    setIsDirty(false);
  }, [templateID]);

  useEffect(() => {
    async function fetchTemplates() {
      setLoading(true);
      try {
        const data = await templateService.getAllTemplates();
        if (Array.isArray(data)) {
          setTemplates(data.map((cell) => ({
            id: cell.id,
            name: cell.name,
            content: cell.content,
          })));
        }
      } catch {
        showNotification("Error al cargar las plantillas", "error");
      } finally {
        setLoading(false);
      }
    }

    async function createAndFetch() {
      try {
        await templateService.createTemplate({ name: "Nueva plantilla" });
        await fetchTemplates();
      } catch {
        showNotification("Error al crear la plantilla", "error");
      } finally {
        setNewTemplate(false);
      }
    }

    if (newTemplate) {
      createAndFetch();
      return;
    }

    if (templates.length === 0 || updateTemplate) {
      fetchTemplates();
      setUpdateTemplate(false);
    }
  }, [newTemplate, updateTemplate]);


  return (
    <DashboardLayout>
      <DashboardNavbar title="Listado de plantillas" />
      <Grid container spacing={2}>
        <Grid item>
          <Grid container spacing={2}>
            <Grid item>
              <MDBox pb={2}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} color="info" />
                  </MDBox>
                ) : (
                  <ListTemplates
                    templates={templates}
                    handleClick={setTemplateID}
                    handleClickNew={setNewTemplate}
                  />
                )}
              </MDBox>
              <ListVariable />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          {selectedTemplate && (
            <MDBox>
              <MDBox pb={2}>
                <ActionTemplate
                  templateId={selectedTemplate.id}
                  templateName={selectedTemplate.name}
                  onTemplateUpdate={setUpdateTemplate}
                  onNameUpdate={handleNameUpdate}
                />
              </MDBox>
              <MDBox>
                <TextEditorPro
                  key={selectedTemplate.id}
                  value={selectedTemplate.content || ""}
                  onChange={handleContentChange}
                  onSave={handleSave}
                  onDirtyChange={setIsDirty}
                  placeholder="Escriba el contenido de la plantilla aquí..."
                />
              </MDBox>
            </MDBox>
          )}
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default Template;
